/**
 * Journal transport.
 *
 * Posting a manual journal is asynchronous by design: the submit records a
 * business event and enqueues it, and acceptance is not proof that anything
 * posted. The screen follows the event until it reaches a terminal state, and
 * says which one it reached.
 */
import type { components } from "@erp/api-client";

import { apiFetchOperational as apiFetch, type Page } from "@/lib/api/http";

type Schemas = components["schemas"];

/** The list publishes summaries — headers with a base total, no lines — and
 *  the detail read publishes the entry with its lines. They are different
 *  shapes and the screen treats them as such. */
export type JournalEntrySummary = Schemas["JournalEntrySummary"];
export type JournalEntry = Schemas["JournalEntry"];
export type ManualJournalLine = Schemas["ManualJournalEventLine"];
export type BusinessEvent = Schemas["BusinessEventEnvelopeBase"];

export interface JournalListQuery {
  posting_date_from?: string;
  posting_date_to?: string;
  account_id?: string;
  journal_type?: string;
  limit?: number;
  cursor?: string;
}

export function listJournalEntries(
  query: JournalListQuery = {},
  signal?: AbortSignal,
): Promise<Page<JournalEntrySummary>> {
  return apiFetch<Page<JournalEntrySummary>>("/journal-entries", { query: { ...query }, signal });
}

export function getJournalEntry(entryId: string, signal?: AbortSignal): Promise<JournalEntry> {
  return apiFetch<JournalEntry>(`/journal-entries/${encodeURIComponent(entryId)}`, { signal });
}

/** `submitManualJournal` — POST · ledger.journal.post. Answers with the event,
 *  not with a journal entry: nothing has posted yet. */
export function submitManualJournal(
  body: { posting_date: string; lines: ManualJournalLine[] },
  idempotencyKey: string,
): Promise<BusinessEvent> {
  return apiFetch<BusinessEvent>("/manual-journals", { method: "POST", body, idempotencyKey });
}

export function getBusinessEvent(eventId: string, signal?: AbortSignal): Promise<BusinessEvent> {
  return apiFetch<BusinessEvent>(`/business-events/${encodeURIComponent(eventId)}`, { signal });
}

/** `reverseJournalEntry` — POST · ledger.journal.reverse. A posted entry is
 *  never edited or deleted; it is reversed by an entry that says so. */
export function reverseJournalEntry(
  entryId: string,
  body: { posting_date?: string; reason?: string },
  idempotencyKey: string,
): Promise<JournalEntry> {
  return apiFetch<JournalEntry>(`/journal-entries/${encodeURIComponent(entryId)}/reverse`, {
    method: "POST",
    body,
    idempotencyKey,
  });
}
